import * as Sentry from "@sentry/react";
import { Link, NavLink, Outlet, useMatch, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function TabErrorFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12">
      <p className="text-lg font-medium">{t("projectPortal.tabError")}</p>
      <button
        onClick={() => window.location.reload()}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        {t("projectPortal.reload")}
      </button>
    </div>
  );
}

export { TabErrorFallback };

interface PortalTab {
  to: string;
  label: string;
  end: boolean;
  disabled?: boolean;
}

export const PORTAL_TABS: readonly PortalTab[] = [
  { to: "overview", label: "projectPortal.tabs.overview", end: true },
  { to: "pipelines", label: "projectPortal.tabs.pipelines", end: false },
  { to: "activity", label: "projectPortal.tabs.activity", end: false },
  {
    to: "governance",
    label: "projectPortal.tabs.governance",
    end: false,
  },
  { to: "graph", label: "projectPortal.tabs.graph", end: false },
  { to: "audit", label: "projectPortal.tabs.audit", end: false },
  {
    to: "metrics",
    label: "projectPortal.tabs.metrics",
    end: false,
  },
  {
    to: "adoption",
    label: "projectPortal.tabs.adoption",
    end: false,
  },
  {
    to: "config",
    label: "projectPortal.tabs.config",
    end: false,
  },
];

export function PortalBreadcrumbs() {
  const { orgSlug, projectSlug } = useParams<{ orgSlug: string; projectSlug: string }>();
  const match = useMatch("/:orgSlug/projects/:projectSlug/:tab/*");
  const { t } = useTranslation();
  const activeTab = PORTAL_TABS.find((tab) => tab.to === match?.params.tab);

  return (
    <nav aria-label={t("projectPortal.breadcrumb.label")}>
      <ol className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <li className="hidden items-center gap-1.5 sm:flex">
          <Link to={`/${orgSlug}`} className="transition-colors hover:text-foreground">
            {orgSlug}
          </Link>
          <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
        </li>
        <li className="flex items-center gap-1.5">
          <Link
            to={`/${orgSlug}/projects/${projectSlug}`}
            className="transition-colors hover:text-foreground"
          >
            {projectSlug}
          </Link>
          {activeTab && <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />}
        </li>
        {activeTab && (
          <li aria-current="page" className="font-medium text-foreground">
            {t(activeTab.label)}
          </li>
        )}
      </ol>
    </nav>
  );
}

export function ProjectPortalLayout() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <PortalBreadcrumbs />
      <h1 className="text-2xl font-bold">{projectSlug}</h1>
      <TooltipProvider>
        <nav className="flex gap-1 border-b" role="tablist">
          {PORTAL_TABS.map(({ to, label, end, disabled }) =>
            disabled ? (
              <Tooltip key={label}>
                <TooltipTrigger asChild>
                  <span
                    className="border-b-2 border-transparent px-4 py-2 text-sm font-medium text-muted-foreground/50 cursor-not-allowed"
                    role="tab"
                    aria-disabled="true"
                  >
                    {t(label)}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{t("projectPortal.comingSoon")}</TooltipContent>
              </Tooltip>
            ) : (
              <NavLink
                key={label}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )
                }
              >
                {t(label)}
              </NavLink>
            ),
          )}
        </nav>
      </TooltipProvider>
      <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
        <Outlet />
      </Sentry.ErrorBoundary>
    </div>
  );
}
